import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { Document, Page } from "react-pdf";
import PdfToolbar from "../molecules/PdfToolbar";
import { RootState } from "../../configureStore";
import { ExtractTask, ReturnActive, TaxReturnPage } from "../../interface/returns";
import useIsMobile from "../../hooks/useIsMobile";
import usePdfZoom from "../../hooks/usePdfZoom";
import { recordPdfLoaded } from "../../utils/taskPdfTiming";

interface PdfPanelProps {
    filteredPages: TaxReturnPage[];
    currentPageIndex: number;
    isCurrentPageRemoved: boolean;
    hasCurrentPageData: boolean;
    selectedFormType: string;
    viewAllPages: boolean;
    onPreviousPage: () => void;
    onNextPage: () => void;
    setFormOnly: (value: boolean) => void;
}

const PdfPanel = ({
    filteredPages,
    currentPageIndex,
    isCurrentPageRemoved,
    hasCurrentPageData,
    selectedFormType,
    viewAllPages,
    onPreviousPage,
    onNextPage,
    setFormOnly,
}: PdfPanelProps) => {
    const isMobile = useIsMobile();
    const [searchParams] = useSearchParams();
    const activePdf = useSelector<RootState, string | undefined>(({ returns }) => returns.activePdf);
    const activeReturnTask = useSelector<RootState, ReturnActive | undefined>(
        ({ returns }) => returns.activeReturnTask,
    );
    const extractTask = activeReturnTask?.type === "extract" ? (activeReturnTask as ExtractTask) : undefined;
    const pageNumberMap = (extractTask?.blobs?.json ?? [])
        .filter((p) => p.isOnList)
        .reduce((map, p, i) => map.set(p.pageNumber, i), new Map<number, number>());
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const [naturalPageWidth, setNaturalPageWidth] = useState<number | null>(null);
    const [numPages, setNumPages] = useState(0);
    const [rotations, setRotations] = useState<Map<number, number>>(new Map());
    const [rotateAllPages, setRotateAllPages] = useState(false);
    const pdfContainerRef = useRef<HTMLDivElement>(null);
    const { zoomPercent, zoomIn, zoomOut, reset: resetZoom } = usePdfZoom('extract');

    const displayedPageNumber = (pageNumberMap.get(filteredPages[currentPageIndex]?.pageNumber || 0) ?? 0) + 1;

    const fitScale = useMemo(() => {
        if (!containerWidth || !naturalPageWidth) return null;
        return containerWidth / naturalPageWidth;
    }, [containerWidth, naturalPageWidth]);

    const effectiveScale = useMemo(() => {
        if (!fitScale) return zoomPercent / 100;
        return fitScale * (zoomPercent / 100);
    }, [fitScale, zoomPercent]);

    const onDocumentLoadSuccess = useCallback(({ numPages: num }: { numPages: number }) => {
        setNumPages(num);
        recordPdfLoaded(searchParams.get("key"));
    }, [searchParams]);

    const onPageRenderSuccess = useCallback(({ originalWidth }: { originalWidth: number }) => {
        setNaturalPageWidth(originalWidth);
        const el = pdfContainerRef.current;
        if (el) {
            const w = el.clientWidth;
            if (w > 0) setContainerWidth(w);
        }
    }, []);

    useEffect(() => {
        const el = pdfContainerRef.current;
        if (!el) return undefined;
        const observer = new ResizeObserver(() => {
            const w = el.clientWidth;
            if (w > 0) setContainerWidth(w);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const handleOpenPdf = useCallback(() => {
        const params = new URLSearchParams(searchParams);
        params.set("pdf", "true");
        window.open(`${window.location.pathname}?${params.toString()}`, "_blank");
        setFormOnly(true);
    }, [searchParams, setFormOnly]);

    const applyRotation = useCallback((delta: number) => {
        setRotations((prev) => {
            const current = prev.get(displayedPageNumber) ?? 0;
            const next = (current + delta + 360) % 360;
            const updated = new Map(prev);
            if (rotateAllPages) {
                for (let p = 1; p <= numPages; p++) {
                    updated.set(p, next);
                }
            } else {
                updated.set(displayedPageNumber, next);
            }
            return updated;
        });
    }, [displayedPageNumber, numPages, rotateAllPages]);

    const handleRotateLeft = useCallback(() => {
        applyRotation(-90);
    }, [applyRotation]);

    const handleRotateRight = useCallback(() => {
        applyRotation(90);
    }, [applyRotation]);

    const toggleRotateAllPages = useCallback(() => {
        setRotateAllPages((prev) => !prev);
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }
            if (e.key === 'ArrowLeft' && e.shiftKey) {
                e.preventDefault();
                handleRotateLeft();
            } else if (e.key === 'ArrowRight' && e.shiftKey) {
                e.preventDefault();
                handleRotateRight();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleRotateLeft, handleRotateRight]);

    return (
        <>
            <PdfToolbar
                isMobile={isMobile}
                viewAllPages={viewAllPages}
                selectedFormType={selectedFormType}
                filteredPagesLength={filteredPages.length}
                currentPageIndex={currentPageIndex}
                isCurrentPageRemoved={isCurrentPageRemoved}
                hasCurrentPageData={hasCurrentPageData}
                zoomPercent={zoomPercent}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onFitWidth={resetZoom}
                onPreviousPage={onPreviousPage}
                onNextPage={onNextPage}
                onOpenPdf={handleOpenPdf}
                onRotateLeft={handleRotateLeft}
                onRotateRight={handleRotateRight}
                rotateAllPages={rotateAllPages}
                onToggleRotateAllPages={toggleRotateAllPages}
            />

            {/* PDF Display */}
            <Paper
                sx={{
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: (t) =>
                        t.palette.mode === "dark" ? t.palette.background.paper : t.palette.background.paper,
                    borderRadius: 1,
                    overflow: "none",
                    p: 2,
                    pb: 0,
                    pr: 0.5,
                }}
            >
                <Box
                    ref={pdfContainerRef}
                    sx={{ height: "100%", overflow: "auto", pr: 1 }}
                >
                    {activePdf && filteredPages.length > 0 && (
                        <Box sx={{ position: "relative", display: "inline-block" }}>
                            <Document
                                file={activePdf}
                                onLoadSuccess={onDocumentLoadSuccess}
                                loading={<CircularProgress />}
                            >
                                <Page
                                    pageNumber={displayedPageNumber}
                                    scale={effectiveScale}
                                    onRenderSuccess={onPageRenderSuccess}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    rotate={rotations.get(displayedPageNumber) ?? 0}
                                />
                            </Document>
                            {isCurrentPageRemoved && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: "rgba(211, 47, 47, 0.12)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        pointerEvents: "none",
                                    }}
                                >
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            color: "error.main",
                                            fontWeight: 800,
                                            opacity: 0.4,
                                            transform: "rotate(-30deg)",
                                            userSelect: "none",
                                            letterSpacing: 8,
                                        }}
                                    >
                                        REMOVED
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                    {((!selectedFormType && !viewAllPages) || filteredPages.length === 0) && (
                        <Typography
                            variant="body1"
                            color="text.secondary"
                        >
                            {!selectedFormType && !viewAllPages
                                ? "Please select a form type or view entire PDF"
                                : "No pages found for this form type"}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </>
    );
};

export default PdfPanel;
